/**
 * The NAMES under the map's markers (owner, QA 24.10.2026: «на маркерах только ★/О/Н/Я и номера — названия нет»).
 *
 * One text rule for every renderer (MapGL markers, the SVG sketch), pure, so a test pins it:
 * - a stop of a plan or of a chain — «номер + название» («2 Самсон»), the same number the card's list has;
 * - any other point — its name;
 * - a point with NO name of its own — «Туалет», «Кассы и вход», «Вид», a cafe's bare type — «тип · ориентир»:
 *   the nearest NAMED object of the museum's catalogue within `LANDMARK_RADIUS_M` («Туалет · Римские фонтаны»;
 *   the landmark's name as it is, no case — «у Римские фонтаны» would be wrong Russian); no landmark, the type;
 * - the visitor — a word («Вы», «Начало: …»).
 * A name is clipped to `LABEL_MAX_CHARS`; the whole text stays in the marker's `title`.
 *
 * And the one geometric rule: labels that would lie on each other (or on another marker's disc, or past the map's
 * edge) are not drawn — the marker stays — and come back when the map is closer (`layoutLabels`).
 */
import { stripEmoji } from '../lib/emoji';
import { distanceMeters } from './navigation';
import { MUSEUM_POINTS, type MuseumPoint } from './objects';

/** A landmark further than this is no landmark: «Туалет · Большой каскад» for a toilet 600 m away misleads. */
export const LANDMARK_RADIUS_M = 250;
/** Longer names are clipped with «…» (the SVG cannot clamp lines; the HTML marker also clamps by CSS). */
export const LABEL_MAX_CHARS = 40;
const TYPE_SEP = ' · ';

/** The catalogue's icon types whose title is only the TYPE of a place: a toilet, the tickets and entrance, a view. */
const TYPE_ICONS = new Set(['w', 'e', 'v']);
/** Services that may carry a name or not: only a title the catalogue repeats is a type («Бистро: гриль» ×6). */
const SERVICE_ICONS = new Set(['c', 'u', 's']);
/** The objects a point can be placed by: fountains and museums/pavilions. */
const LANDMARK_ICONS = new Set(['f', 'm']);
/** Bare type words a single catalogue row may still carry («Ресторан»). */
const BARE_TYPES = new Set(['кафе', 'ресторан', 'туалет', 'вид', 'мороженое', 'бистро', 'касса', 'кассы', 'вход']);

/** A title as it is drawn: no emoji, non-breaking spaces as spaces, blanks collapsed. */
export function plainTitle(title: string): string {
  return stripEmoji(title).replace(/[  ]/g, ' ').replace(/\s+/g, ' ').trim();
}

/** The catalogue's titles that name a TYPE: a service title repeated among the objects of its icon. */
function catalogueTypeTitles(points: readonly MuseumPoint[]): Set<string> {
  const seen = new Map<string, number>();
  for (const point of points) {
    if (!SERVICE_ICONS.has(point.iconType)) continue;
    const key = `${point.iconType}|${plainTitle(point.label).toLowerCase()}`;
    seen.set(key, (seen.get(key) ?? 0) + 1);
  }
  return new Set([...seen].filter(([, count]) => count > 1).map(([key]) => key.slice(key.indexOf('|') + 1)));
}
const TYPE_TITLES = catalogueTypeTitles(MUSEUM_POINTS);
const LANDMARKS = MUSEUM_POINTS.filter((point) => LANDMARK_ICONS.has(point.iconType));

/** The titles a frame repeats («Туалет» ×3 on one map) — lower-cased, for `isTypeOnly`. */
export function repeatedTitles(titles: readonly string[]): Set<string> {
  const seen = new Map<string, number>();
  for (const title of titles) {
    const key = plainTitle(title).toLowerCase();
    seen.set(key, (seen.get(key) ?? 0) + 1);
  }
  return new Set([...seen].filter(([, count]) => count > 1).map(([key]) => key));
}

/** Does the title name only the TYPE of a place? By the catalogue's icon type, by a bare type word, by a service
 *  title the catalogue repeats, or by a title the frame itself repeats (never for a fountain or a museum). */
export function isTypeOnly(title: string, iconType: string | undefined, repeated: ReadonlySet<string> = new Set(), typeTitles: ReadonlySet<string> = TYPE_TITLES): boolean {
  const key = plainTitle(title).toLowerCase();
  if (!key) return false;
  if (iconType && LANDMARK_ICONS.has(iconType)) return false;
  if (iconType && TYPE_ICONS.has(iconType)) return true;
  return BARE_TYPES.has(key) || (!!iconType && SERVICE_ICONS.has(iconType) && typeTitles.has(key)) || repeated.has(key);
}

/** The nearest named object of the catalogue within `radius` m of the point, never one that is the point itself
 *  (the same title). `null` when none. */
export function nearestLandmark(point: { lat: number; lon: number }, own: string, radius = LANDMARK_RADIUS_M, landmarks: readonly MuseumPoint[] = LANDMARKS): MuseumPoint | null {
  const ownKey = plainTitle(own).toLowerCase();
  let best: MuseumPoint | null = null;
  let bestDistance = radius;
  for (const landmark of landmarks) {
    if (plainTitle(landmark.label).toLowerCase() === ownKey) continue;
    const distance = distanceMeters(point, landmark);
    if (distance <= bestDistance) { best = landmark; bestDistance = distance; }
  }
  return best;
}

export type LabelSubject = {
  title: string;
  point: { lat: number; lon: number };
  /** The catalogue's icon type of the point, when it is one of the museum's. */
  iconType?: string;
  /** A plan's stop or a chain's point: «N Название». */
  number?: number;
};

/** Whole text of a marker's name, before it is clipped. */
export function markerLabel(subject: LabelSubject, repeated: ReadonlySet<string> = new Set(), landmarks: readonly MuseumPoint[] = LANDMARKS): string {
  const title = plainTitle(subject.title);
  let name = title;
  if (title && isTypeOnly(title, subject.iconType, repeated)) {
    const near = nearestLandmark(subject.point, title, LANDMARK_RADIUS_M, landmarks);
    if (near) name = `${title}${TYPE_SEP}${plainTitle(near.label)}`;
  }
  return subject.number !== undefined ? `${subject.number} ${name}`.trim() : name;
}

/** *text* clipped to *max* characters with «…», at a word when there is one near the end. */
export function clipLabel(text: string, max = LABEL_MAX_CHARS): string {
  if (text.length <= max) return text;
  const cut = text.slice(0, max - 1);
  const space = cut.lastIndexOf(' ');
  return `${(space > max * 0.6 ? cut.slice(0, space) : cut).replace(/[\s·,:;-]+$/, '')}…`;
}

/** The visitor's marker: a word, not a sentence. «Начало: …» when the start is a museum object they named. */
export function originMarkerLabel(source: string | undefined, label: string | undefined): string {
  const name = label ? plainTitle(label) : '';
  if (source === 'start') return name ? `Начало: ${name}` : 'Начало';
  if ((source === 'address' || source === 'chat') && name) return name;
  return 'Вы';
}

// ── geometry ──────────────────────────────────────────────────────────────────────────────────────────────

export type Box = { left: number; top: number; right: number; bottom: number };

export function boxesOverlap(a: Box, b: Box, gap = 0): boolean {
  return a.left < b.right + gap && b.left < a.right + gap && a.top < b.bottom + gap && b.top < a.bottom + gap;
}

/** The gap between a disc and its name, px. */
export const LABEL_GAP = 4;
/** Labels closer than this to one another count as touching, px. */
export const LABEL_PAD = 2;

export type LabelItem = {
  /** The marker's disc on the screen. */
  disc: Box;
  /** The name's size (it is the same on either side). */
  width: number;
  height: number;
  /** Lower wins: the start and the finish, then the path's order, then the rest. */
  rank: number;
  /** Distance of the disc from the map's centre — the tie-break: what is near the centre stays. */
  distance: number;
};
export type LabelPlace = { visible: boolean; side: 'right' | 'left' };

/** Where the name lies on a side of its disc. */
export function labelBox(item: Pick<LabelItem, 'disc' | 'width' | 'height'>, side: 'right' | 'left'): Box {
  const middle = (item.disc.top + item.disc.bottom) / 2;
  const left = side === 'right' ? item.disc.right + LABEL_GAP : item.disc.left - LABEL_GAP - item.width;
  return { left, right: left + item.width, top: middle - item.height / 2, bottom: middle + item.height / 2 };
}

const inside = (box: Box, bounds: Box) => box.left >= bounds.left && box.right <= bounds.right && box.top >= bounds.top && box.bottom <= bounds.bottom;

/**
 * Which names are drawn. Greedy by priority: a name takes the right of its disc, or the left when the right runs
 * past the map's edge; it is hidden when neither fits, when it would touch a name already shown, or when it would
 * lie on ANOTHER marker's disc. The discs themselves are never hidden here.
 */
export function layoutLabels(items: readonly LabelItem[], bounds: Box): LabelPlace[] {
  const places: LabelPlace[] = items.map(() => ({ visible: false, side: 'right' }));
  const shown: Box[] = [];
  const order = items.map((_, index) => index).sort((a, b) => items[a]!.rank - items[b]!.rank || items[a]!.distance - items[b]!.distance || a - b);
  for (const index of order) {
    const item = items[index]!;
    for (const side of ['right', 'left'] as const) {
      const box = labelBox(item, side);
      if (!inside(box, bounds)) continue;
      if (shown.some((other) => boxesOverlap(box, other, LABEL_PAD))) continue;
      if (items.some((other, at) => at !== index && boxesOverlap(box, other.disc))) continue;
      places[index] = { visible: true, side };
      shown.push(box);
      break;
    }
  }
  return places;
}

// ── the SVG sketch (no MapGL) ────────────────────────────────────────────────────────────────────────────

/** The sketch's coordinate box (`viewBox`), and its disc radius. */
export const SKETCH_SIZE = { width: 360, height: 200 };
export const SKETCH_DISC_R = 9;
/** SVG text cannot clamp lines: a name is clipped to this many characters. */
export const SKETCH_LABEL_CHARS = 22;
/** What a character of the sketch's name takes (12.5 px semi-bold Cyrillic), and the plate's height. */
const SKETCH_CHAR_W = 6.9;
const SKETCH_LABEL_H = 16;

export type SketchName = { x: number; y: number; text: string; rank: number };
export type SketchPlaced = { x: number; y: number; text: string; anchor: 'start' | 'end' };

/** Where the sketch's names go, aligned with *names*; `null` for one that is not drawn. The same rule as the map's:
 *  right of the disc or left at the edge, hidden when it would lie on another name or disc or leave the box. */
export function sketchLabels(names: readonly SketchName[], size = SKETCH_SIZE): (SketchPlaced | null)[] {
  const texts = names.map((name) => clipLabel(name.text, SKETCH_LABEL_CHARS));
  const items: LabelItem[] = names.map((name, index) => ({
    disc: { left: name.x - SKETCH_DISC_R, right: name.x + SKETCH_DISC_R, top: name.y - SKETCH_DISC_R, bottom: name.y + SKETCH_DISC_R },
    width: texts[index]!.length * SKETCH_CHAR_W + 4, height: SKETCH_LABEL_H, rank: name.rank,
    distance: Math.hypot(name.x - size.width / 2, name.y - size.height / 2),
  }));
  const bounds = { left: 0, top: 0, right: size.width, bottom: size.height };
  return layoutLabels(items, bounds).map((place, index) => {
    if (!place.visible || !texts[index]) return null;
    const box = labelBox(items[index]!, place.side);
    return place.side === 'right' ? { x: box.left + 2, y: names[index]!.y + 4.5, text: texts[index]!, anchor: 'start' } : { x: box.right - 2, y: names[index]!.y + 4.5, text: texts[index]!, anchor: 'end' };
  });
}
